import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import { formatTime12Hour, parseToYYYYMMDD } from "../../utils/dateHelpers";

const FreelancerBookingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [refundType, setRefundType] = useState("full");
  const [useCredit, setUseCredit] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
  const bookingsBasePath = location.pathname.startsWith(
    "/dashboard/booth-renter",
  )
    ? "/dashboard/booth-renter/bookings"
    : "/dashboard/freelancer/bookings";

  useEffect(() => {
    fetchBookingDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBookingDetail = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        navigate("/login");
        return;
      }
      // Use freelancer bookings endpoint which includes serviceName
      const response = await fetch(`${API_BASE_URL}/api/freelancer/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        const foundBooking = data.bookings.find((b) => b._id === id);
        if (foundBooking) {
          setBooking(foundBooking);
        } else {
          setError("Booking not found");
        }
      } else {
        setError("Failed to load booking details");
      }
    } catch (err) {
      setError("Failed to load booking details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const normalizedDate = parseToYYYYMMDD(dateString);
    if (!normalizedDate) return "N/A";

    const [year, month, day] = normalizedDate.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    if (Number.isNaN(date.getTime())) {
      return "N/A";
    }

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (time24) => {
    return formatTime12Hour(time24) || "N/A";
  };

  const getAppointmentDateTime = (dateValue, timeValue) => {
    const normalizedDate = parseToYYYYMMDD(dateValue);
    if (!normalizedDate) return null;

    const [year, month, day] = normalizedDate.split("-").map(Number);
    const [hours = 0, minutes = 0] = String(timeValue || "00:00")
      .split(":")
      .map(Number);
    const appointmentDateTime = new Date(
      year,
      month - 1,
      day,
      Number.isNaN(hours) ? 0 : hours,
      Number.isNaN(minutes) ? 0 : minutes,
    );

    return Number.isNaN(appointmentDateTime.getTime())
      ? null
      : appointmentDateTime;
  };

  const handleCompleteBooking = async () => {
    // Prevent completing future bookings
    const appointmentDateTime = getAppointmentDateTime(
      booking.appointmentDate,
      booking.startTime,
    );
    const now = new Date();

    if (!appointmentDateTime) {
      alert("Unable to determine the appointment time for this booking.");
      return;
    }
    if (appointmentDateTime > now) {
      alert(
        "Cannot mark a future booking as complete. Please wait until the appointment time has passed.",
      );
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${API_BASE_URL}/api/bookings/${id}/status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: "completed" }),
        },
      );

      if (response.ok) {
        alert("Booking marked as complete");
        fetchBookingDetail();
      } else {
        alert("Failed to complete booking");
      }
    } catch (err) {
      alert("Failed to complete booking");
    }
  };

  const handleNoShowBooking = async () => {
    // Prevent marking future bookings as no-show
    const appointmentDateTime = getAppointmentDateTime(
      booking.appointmentDate,
      booking.startTime,
    );

    if (!appointmentDateTime) {
      alert("Unable to determine the appointment time for this booking.");
      return;
    }

    if (appointmentDateTime > new Date()) {
      alert(
        "Cannot mark a future booking as no-show. Please wait until the appointment time has passed.",
      );
      return;
    }

    if (
      !window.confirm(
        "Mark this booking as no-show? Customer will not receive a refund.",
      )
    )
      return;

    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${API_BASE_URL}/api/bookings/${id}/status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: "no-show" }),
        },
      );

      if (response.ok) {
        alert("Booking marked as no-show");
        fetchBookingDetail();
      } else {
        alert("Failed to mark no-show");
      }
    } catch (err) {
      alert("Failed to mark no-show");
    }
  };

  const calculateRefundAmount = () => {
    if (!booking) return { amount: 0, percentage: 0 };

    const appointmentDate = getAppointmentDateTime(
      booking.appointmentDate || booking.bookingDate,
      booking.startTime || booking.bookingTime,
    );
    if (!appointmentDate) return { amount: 0, percentage: 0, hoursUntil: 0 };

    const now = new Date();
    const hoursUntil = (appointmentDate - now) / (1000 * 60 * 60);

    const baseAmount = booking.servicePriceAtBooking || booking.price || 0;
    let percentage = 0;

    if (refundType === "none") {
      percentage = 0;
    } else if (refundType === "partial") {
      percentage = 50;
    } else {
      // full
      if (hoursUntil < 24) percentage = 0;
      else if (hoursUntil >= 24 && hoursUntil < 48) percentage = 50;
      else percentage = 80;
    }

    return { amount: baseAmount * (percentage / 100), percentage, hoursUntil };
  };

  const getCancellationPayoutAmount = () => {
    if (!booking) return null;

    const canonicalCancellationPayout =
      Number(booking.cancellationPayoutAmount) || 0;
    if (canonicalCancellationPayout > 0) {
      return canonicalCancellationPayout;
    }

    if (booking.status !== "cancelled" && booking.status !== "canceled") {
      return null;
    }

    const distribution = booking.cancellationFeeDistribution;
    if (!distribution) return null;

    // The professional's shares ADDED, matching utils/paymentSplitCalculator.js.
    // ownerShare is deliberately excluded - it is the BUSINESS's share.
    const payoutAmount =
      (Number(distribution.freelancerShare) || 0) +
      (Number(distribution.professionalShare) || 0);

    return payoutAmount > 0 ? payoutAmount : null;
  };

  const displayAmount = getCancellationPayoutAmount();

  const handleCancelBooking = async () => {
    if (!cancelReason.trim()) {
      alert("Please provide a cancellation reason");
      return;
    }

    const refundCalc = calculateRefundAmount();

    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(
        `${API_BASE_URL}/api/bookings/${id}/cancel`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reason: cancelReason,
            refundType: refundType,
            refundPercent: refundCalc.percentage,
            useCredit,
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        alert(
          `Booking cancelled successfully. Refund: $${data.refundAmount?.toFixed(2) || "0.00"}`,
        );
        setShowCancelModal(false);
        fetchBookingDetail();
      } else {
        const errorData = await response.json();
        alert(errorData.message || "Failed to cancel booking");
      }
    } catch (err) {
      alert("Failed to cancel booking");
    }
  };

  const handleRescheduleBooking = async () => {
    if (!newDate || !newTime) {
      alert("Please select both date and time");
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await fetch(`${API_BASE_URL}/api/bookings/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          appointmentDate: newDate,
          startTime: newTime,
        }),
      });

      if (response.ok) {
        alert("Booking rescheduled successfully");
        setShowRescheduleModal(false);
        fetchBookingDetail();
      } else {
        const errorData = await response.json();
        alert(errorData.message || "Failed to reschedule booking");
      }
    } catch (err) {
      alert("Failed to reschedule booking");
    }
  };

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading booking details...</p>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  if (error || !booking) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p style={{ color: "#ef4444" }}>{error || "Booking not found"}</p>
          <button
            onClick={() => navigate(bookingsBasePath)}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              background: "#001d4a",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Back to Bookings
          </button>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  return (
    <FreelancerSidebarLayout>
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          padding: isMobile ? "15px" : "20px",
        }}
      >
        <button
          onClick={() => navigate(bookingsBasePath)}
          style={{
            marginBottom: "20px",
            padding: "8px 16px",
            background: "#fff",
            color: "#001d4a",
            border: "1px solid #001d4a",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          ← Back to Bookings
        </button>

        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            padding: isMobile ? "20px" : "30px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <h1 style={{ fontSize: isMobile ? "20px" : "24px", margin: 0 }}>
              Booking Details
            </h1>
            <span
              style={{
                padding: "6px 16px",
                borderRadius: "20px",
                fontSize: "14px",
                fontWeight: "500",
                background:
                  booking.status === "confirmed"
                    ? "#d1fae5"
                    : booking.status === "completed"
                      ? "#dbeafe"
                      : booking.status === "cancelled"
                        ? "#fee2e2"
                        : "#fef3c7",
                color:
                  booking.status === "confirmed"
                    ? "#065f46"
                    : booking.status === "completed"
                      ? "#1e40af"
                      : booking.status === "cancelled"
                        ? "#991b1b"
                        : "#92400e",
                textTransform: "capitalize",
              }}
            >
              {booking.status}
            </span>
          </div>

          <div style={{ marginBottom: "30px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "15px",
                color: "#001d4a",
              }}
            >
              Appointment Information
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "15px",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    marginBottom: "4px",
                  }}
                >
                  Service
                </div>
                <div style={{ fontSize: "15px", fontWeight: "500" }}>
                  {booking.serviceName || "N/A"}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    marginBottom: "4px",
                  }}
                >
                  Duration
                </div>
                <div style={{ fontSize: "15px", fontWeight: "500" }}>
                  {booking.duration || 60} minutes
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    marginBottom: "4px",
                  }}
                >
                  Date
                </div>
                <div style={{ fontSize: "15px", fontWeight: "500" }}>
                  {formatDate(booking.appointmentDate || booking.bookingDate)}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    marginBottom: "4px",
                  }}
                >
                  Time
                </div>
                <div style={{ fontSize: "15px", fontWeight: "500" }}>
                  {formatTime(booking.startTime || booking.bookingTime)} -{" "}
                  {formatTime(booking.endTime)}
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: "30px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "15px",
                color: "#001d4a",
              }}
            >
              Customer Information
            </h3>
            <div style={{ display: "grid", gap: "15px" }}>
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    marginBottom: "4px",
                  }}
                >
                  Name
                </div>
                <div style={{ fontSize: "15px", fontWeight: "500" }}>
                  {booking.customerName || booking.clientName || "N/A"}
                </div>
              </div>
              {(booking.customerEmail || booking.clientEmail) && (
                <div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      marginBottom: "4px",
                    }}
                  >
                    Email
                  </div>
                  <div style={{ fontSize: "15px", fontWeight: "500" }}>
                    {booking.customerEmail || booking.clientEmail}
                  </div>
                </div>
              )}
              {(booking.customerPhone || booking.clientPhone) && (
                <div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      marginBottom: "4px",
                    }}
                  >
                    Phone
                  </div>
                  <div style={{ fontSize: "15px", fontWeight: "500" }}>
                    {booking.customerPhone || booking.clientPhone}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: "30px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "15px",
                color: "#001d4a",
              }}
            >
              Payment Summary
            </h3>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "15px",
                background: "#f9fafb",
                borderRadius: "8px",
              }}
            >
              <div style={{ fontSize: "15px", fontWeight: "500" }}>
                {getCancellationPayoutAmount() !== null
                  ? "Cancellation Payout"
                  : "Service Total"}
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#001d4a",
                }}
              >
                $
                {displayAmount !== null
                  ? displayAmount.toFixed(2)
                  : booking.servicePriceAtBooking || booking.price || "0.00"}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "15px",
                borderTop: "2px solid #e5e7eb",
                marginTop: "10px",
              }}
            >
              <div style={{ fontSize: "16px", fontWeight: "600" }}>Total</div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#2563eb",
                }}
              >
                $
                {displayAmount !== null
                  ? displayAmount.toFixed(2)
                  : booking.totalPrice ||
                    booking.servicePriceAtBooking ||
                    booking.price ||
                    "0.00"}
              </div>
            </div>
          </div>

          {(booking.staffNotes || booking.notes) && (
            <div style={{ marginBottom: "30px" }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "15px",
                  color: "#001d4a",
                }}
              >
                Notes
              </h3>
              <div
                style={{
                  padding: "15px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#4b5563",
                }}
              >
                {booking.staffNotes || booking.notes}
              </div>
            </div>
          )}

          {(booking.status === "confirmed" || booking.status === "pending") && (
            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                marginBottom: "15px",
              }}
            >
              <button
                onClick={handleCompleteBooking}
                style={{
                  flex: 1,
                  minWidth: isMobile ? "100%" : "150px",
                  padding: "12px 24px",
                  background: "#10b981",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "600",
                }}
              >
                Mark Complete
              </button>
              <button
                onClick={() => setShowRescheduleModal(true)}
                style={{
                  flex: 1,
                  minWidth: isMobile ? "100%" : "150px",
                  padding: "12px 24px",
                  background: "#3b82f6",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "600",
                }}
              >
                Reschedule
              </button>
            </div>
          )}

          {(booking.status === "confirmed" || booking.status === "pending") && (
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                onClick={handleNoShowBooking}
                style={{
                  flex: 1,
                  minWidth: isMobile ? "100%" : "150px",
                  padding: "12px 24px",
                  background: "#fff",
                  color: "#6b7280",
                  border: "2px solid #d1d5db",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "600",
                }}
              >
                Mark No-Show
              </button>
              <button
                onClick={() => setShowCancelModal(true)}
                style={{
                  flex: 1,
                  minWidth: isMobile ? "100%" : "150px",
                  padding: "12px 24px",
                  background: "#fff",
                  color: "#ef4444",
                  border: "2px solid #ef4444",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "600",
                }}
              >
                Cancel Booking
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "30px",
              maxWidth: "500px",
              width: "100%",
            }}
          >
            <h3
              style={{ marginTop: 0, marginBottom: "20px", color: "#ef4444" }}
            >
              Cancel Booking
            </h3>

            {/* Refund Preview */}
            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #f59e0b",
                borderRadius: "8px",
                padding: "15px",
                marginBottom: "20px",
                fontSize: "14px",
              }}
            >
              <div style={{ fontWeight: "600", marginBottom: "8px" }}>
                Refund Preview:
              </div>
              {(() => {
                const { amount, percentage, hoursUntil } =
                  calculateRefundAmount();
                return (
                  <>
                    <div>
                      Hours until appointment: {hoursUntil.toFixed(1)}hrs
                    </div>
                    <div
                      style={{
                        marginTop: "5px",
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#d97706",
                      }}
                    >
                      {percentage}% refund = ${amount.toFixed(2)}
                    </div>
                    {hoursUntil < 24 && (
                      <div
                        style={{
                          marginTop: "5px",
                          color: "#dc2626",
                          fontSize: "13px",
                        }}
                      >
                        ⚠️ Less than 24 hours notice - No refund policy
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                Refund Type:
              </label>
              <select
                value={refundType}
                onChange={(e) => setRefundType(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="full">Auto-calculate (time-based)</option>
                <option value="partial">50% Partial Refund</option>
                <option value="none">No Refund</option>
              </select>
            </div>

            <p
              style={{
                marginBottom: "15px",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Please provide a reason for cancelling this booking:
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Cancellation reason (required)..."
              style={{
                width: "100%",
                minHeight: "100px",
                padding: "12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                fontFamily: "inherit",
                marginBottom: "16px",
                boxSizing: "border-box",
              }}
            />

            {/* Store Credit Option - Only show if refund amount > 0 */}
            {refundType !== "none" &&
              (() => {
                const refundCalc = calculateRefundAmount();
                if (refundCalc.amount === 0) return null;

                return (
                  <div
                    style={{
                      marginBottom: "20px",
                      padding: "16px",
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #86efac",
                      borderRadius: "8px",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        cursor: "pointer",
                        gap: "12px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={useCredit}
                        onChange={(e) => setUseCredit(e.target.checked)}
                        style={{
                          marginTop: "2px",
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#166534",
                            marginBottom: "4px",
                          }}
                        >
                          💰 Issue Store Credit Instead
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#15803d",
                            marginBottom: "8px",
                          }}
                        >
                          Give customer ${refundCalc.amount.toFixed(2)} credit
                          instead of card refund
                        </div>
                        <div style={{ fontSize: "12px", color: "#166534" }}>
                          ✓ Expires in 90 days &nbsp;•&nbsp; ✓ Auto-applies to
                          next booking &nbsp;•&nbsp; ✓ Encourages rebooking
                        </div>
                      </div>
                    </label>
                  </div>
                );
              })()}

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowCancelModal(false)}
                style={{
                  flex: 1,
                  padding: "10px 20px",
                  background: "#fff",
                  color: "#6b7280",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Close
              </button>
              <button
                onClick={handleCancelBooking}
                disabled={!cancelReason.trim()}
                style={{
                  flex: 1,
                  padding: "10px 20px",
                  background: !cancelReason.trim() ? "#d1d5db" : "#ef4444",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: !cancelReason.trim() ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "30px",
              maxWidth: "500px",
              width: "100%",
            }}
          >
            <h3
              style={{ marginTop: 0, marginBottom: "20px", color: "#3b82f6" }}
            >
              Reschedule Booking
            </h3>
            <p style={{ marginBottom: "20px", color: "#6b7280" }}>
              Select new date and time:
            </p>

            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                New Date:
              </label>
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                New Time:
              </label>
              <input
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowRescheduleModal(false)}
                style={{
                  flex: 1,
                  padding: "10px 20px",
                  background: "#fff",
                  color: "#6b7280",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Close
              </button>
              <button
                onClick={handleRescheduleBooking}
                disabled={!newDate || !newTime}
                style={{
                  flex: 1,
                  padding: "10px 20px",
                  background: !newDate || !newTime ? "#d1d5db" : "#3b82f6",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: !newDate || !newTime ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                Confirm Reschedule
              </button>
            </div>
          </div>
        </div>
      )}
    </FreelancerSidebarLayout>
  );
};

export default FreelancerBookingDetail;
